/**
 * VoicemailPlayer.js
 * Talkroute
 *
 * Created by Vel Genov on 2/28/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Grid, Button, Loader, Segment, Dimmer, Icon } from "semantic-ui-react";
import Slider from "@material-ui/core/Slider";
import { domClasses, domIds } from "@constants/testConstants";
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded';
import PauseRoundedIcon from '@material-ui/icons/PauseRounded';
import { formatSeconds } from "../../helpers/converters";
import ErrorBoundary from "../../containers/ErrorBoundary";
import appConfig from "../../config";
import {
  fetchVoicemailFile,
  errorPlayingVoicemail,
} from "../../actions/voicemailActions";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { IconButton } from "@material-ui/core";

const removeVerticalPadding = { paddingTop: "0px", paddingBottom: "0px" };
const removeMargin = { margin: "0px" };

/**
 * Draw the loading icon
 */
const LoadingIcon = () => (
  <Segment style={{ border: "none", boxShadow: "none" }}>
    <Dimmer active inverted>
      <Loader inverted size="medium" />
    </Dimmer>
  </Segment>
);

/**
 * A component that shows Voicemail play controls
 */
interface IVoicemailPlayerProps {
  suggestedVoicemailDuration?: number;
  selectedStyle: {
    backgroundColor?: string;
    opacity?: string;
  };
  markReadCallback: (isRead?: boolean, toggle?: boolean) => any;
}

let audioPlayer: HTMLAudioElement | null;

const VoicemailPlayer = ErrorBoundary(
  (
    {
      selectedStyle,
      suggestedVoicemailDuration,
      markReadCallback,
    }: IVoicemailPlayerProps,
    error
  ) => {
    const [sliderValue, setSliderValue] = useState(0);
    const [loadingVoicemail, setLoadingVoicemail] = useState(false);
    const [voicemailCurrentTime, setVoicemailCurrentTime] = useState(0);
    const [voicemailDuration, setVoicemailDuration] = useState<number>();
    const voicemailId = useAppSelector(
      (state) => state.voicemails.playVoicemail
    );
    const voicemailFilePath = useAppSelector(
      (state) => state.voicemails.playVoicemailFilePath
    );
    const isPreferencesVisible = useAppSelector(
      (state) => state.preferences.isPreferencesVisible
    );
    const defaultAudioOutput = useAppSelector(
      (state) => state.preferences.defaultAudioOutput
    );
    const voicemails = useAppSelector((state) => state.voicemails.data);
    const [componentLoaded, setComponentLoaded] = useState(false);
    const dispatch = useDispatch();
    const [enablePlayer, setEnablePlayer] = useState(false);
    const [shouldUpdateReadStatus, setShouldUpdateReadStatus] = useState(false);

    useEffect(() => {
      setupAudioPlayer();
      updateProgressBarPosition(0);
      setComponentLoaded(true);
      return () => {
        resetPlayer(true);
        setComponentLoaded(false);
        setEnablePlayer(false);
      };
    }, []);

    useEffect(() => {
      resetPlayer(true);
      setupAudioPlayer();
    }, [voicemailId]);

    useEffect(() => {
      if (isPreferencesVisible) {
        pauseVoicemail();
      }
    }, [isPreferencesVisible]);

    useEffect(() => {
      if (voicemailFilePath && componentLoaded) {
        resetPlayer();
        setupAudioPlayer();
        voicemailReady();
      }
    }, [voicemailFilePath]);

    useEffect(() => {
      /**
       * `ontimeupdate` is not connected to react so it will by default not update itself
       * to use latest state. This code ensures that whenever relevant state updates, the
       *  callback is also updated to use the latest value
       */
      if (audioPlayer) {
        audioPlayer.ontimeupdate = () => playTimeUpdated();
      }
    }, [shouldUpdateReadStatus]);

    /**
     * Draw the Voicemail play slider
     * The slider is made of two elements. The input catches all input and displays the handle
     * The progress element is the one that actually displays a progress bar
     */
    const drawSlider = (sliderValue: number) => {
      const disabled = !voicemailFilePath || !enablePlayer;
      return (
        <div style={{ verticalAlign: "middle" }}>
          <Slider
            id={domIds.VOICEMAIL_PLAYER_SLIDER}
            name="points"
            className="slider"
            style={{
              color: "var(--color-pumpkin-orange)",
              paddingTop: 20,
            }}
            min={0}
            max={voicemailDuration}
            step={1}
            value={sliderValue ?? 0}
            disabled={disabled}
            onChange={(e, newValue) => {
              sliderOnInput(newValue);
            }}
            onChangeCommitted={(e, newValue) => {
              sliderOnChange(newValue);
            }}
          />
        </div>
      );
    };

    /**
     * Called when a change on the slider is completed
     *
     * @param event a syntethic event that contains info about the caller
     */
    const sliderOnChange = (value: number | number[]) => {
      const inputDuration = Number(value);
      if (audioPlayer) audioPlayer.currentTime = inputDuration;
      updateProgressBarPosition(inputDuration);
      if (audioPlayer && (audioPlayer.paused || audioPlayer.ended)) {
        playVoicemail();
      }
    };

    /**
     * Called when the slider is dragged
     *
     * @param event a systethic event that contains info about the caller
     */
    const sliderOnInput = (value: number | number[]) => {
      const inputDuration = Number(value);
      pauseVoicemail();
      updateProgressBarPosition(inputDuration);
      if (audioPlayer) {
        audioPlayer.currentTime = inputDuration;
      }
    };

    /**
     * Determine if the voicemail file is being loaded
     * Draw a play button if the file is not being loaded,
     * a loading indicator otherwise
     */
    const drawPlayOrLoading = () => {
      return loadingVoicemail ? drawLoadingIcon() : drawPlayIcon();
    };

    /**
     * Draw the loading icon
     */
    const drawLoadingIcon = () => (
      <Segment style={{ border: "none", boxShadow: "none" }}>
        <Dimmer active inverted>
          <Loader inverted size="medium" />
        </Dimmer>
      </Segment>
    );

    /**
     * Draw the play or pause icon
     */
    const drawPlayIcon = () => {
      const isPlaying = audioPlayer && !audioPlayer.paused;
      const action = isPlaying ? pauseVoicemail : playVoicemail;
      
      return (
        <IconButton
          className={domClasses.VOICEMAIL_PLAYER_PLAY_BUTTON}
          onClick={() => {
            willPlayVoicemail && setEnablePlayer(true);
            action();
          }}
          style={{
            marginRight: 5,
            marginBottom: 2,
            padding: 4,
            transform: "translateX(-10px)"}}
        >
          {isPlaying ? <PauseRoundedIcon
          style={{width: 27, height: 27}} /> : <PlayArrowRoundedIcon
          style={{width: 27, height: 27}} />}
        </IconButton>
      );
    };

    /**
     * Draw the elapsed time display
     */
    const drawElapsedTime = () => {
      return drawTime(voicemailCurrentTime);
    };

    /**
     * Draw the total time display
     */
    const drawTotalTime = () => {
      let voicemailDurationToDraw = suggestedVoicemailDuration;

      if (voicemailDuration) {
        voicemailDurationToDraw = voicemailDuration;
      }

      return drawTime(voicemailDurationToDraw);
    };

    /**
     * Draw a generic time display
     *
     * @param time the time to use when drawing
     */
    const drawTime = (time: number | undefined) => {
      const roundedTime = Math.floor(time as number);
      const formattedTime = formatSeconds(roundedTime.toString());

      return (
        <div id={domIds.VOICEMAIL_PLAYER_ELAPSED_TIME}>{formattedTime}</div>
      );
    };

    /**
     * Create the audio player and set up an input file
     */
    const setupAudioPlayer = () => {
      audioPlayer = new Audio();
      audioPlayer.ontimeupdate = () => playTimeUpdated(); // these functions should not directly depend on state
      audioPlayer.onended = donePlayingVoicemail; // as the state will not be up to date after rerender
      audioPlayer.onloadedmetadata = () => audioPlayerMetaDataReady(); //
      getDuration();
    };

    /**
     * A callback triggered when the play time has been updated by the audio player
     */
    const playTimeUpdated = () => {
      if (audioPlayer) {
        const { currentTime } = audioPlayer;
        if (!audioPlayer.seeking) {
          updateProgressBarPosition(currentTime);
        }
        updateCurrentTime(Math.floor(currentTime));
      }
    };

    /**
     * Updates the progress bar position
     *
     * @param percent a progress percent indicator. Values from 0 - 1
     */
    const updateProgressBarPosition = (currentDuration: number) => {
      setSliderValue(currentDuration);
      if (voicemailDuration && currentDuration >= 0) {
        const calculatedPercent = Math.floor(
          (currentDuration / voicemailDuration) * 100
        );
        if (calculatedPercent > 35) {
          // If the user is over 35%, mark the voicemail as read
          markVoicemailAsRead();
        }
      }
    };

    /**
     * Request to mark a voicemail as read
     */
    const markVoicemailAsRead = () => {
      if (shouldUpdateReadStatus) {
        setShouldUpdateReadStatus(false);
        markReadCallback();
      }
    };

    /**
     * Update the current time variable
     *
     * @param currentTime the current play time in seconds
     */
    const updateCurrentTime = (currentTime: number) => {
      if (voicemailCurrentTime !== currentTime) {
        // Only update if necessary to avoid changing the state too often
        setVoicemailCurrentTime(currentTime);
      }
    };

    /**
     * Get the voicemail duration from the voicemail object
     */
    const getDuration = () => {
      const voicemail = voicemails.find(
        (voicemailElement) => voicemailId === voicemailElement.id
      );

      if (voicemail) {
        const { duration } = voicemail;
        setVoicemailDuration(duration);
      }
    };

    /**
     * Plays the voicemail. Start loading it if it's not available
     */
    const playVoicemail = () => {
      !shouldUpdateReadStatus && setShouldUpdateReadStatus(true);
      if (voicemailFilePath && audioPlayer && audioPlayer.src) {
          if ("setSinkId" in HTMLMediaElement.prototype) {
            (audioPlayer as any)
              .setSinkId(defaultAudioOutput)
              .then(() => {
                  audioPlayer?.play();
              })
              .catch((error) => {
                handleError(error);
              });
          } else {
              audioPlayer?.play();
          }
      } else {
        fetchVoicemail();
      }
    };

    /**
     * Pause the voicemail player
     */
    const pauseVoicemail = () => {
      if (audioPlayer) {
        audioPlayer.pause();
      }
    };

    /**
     * Called when the test sound is finished playing
     */
    const donePlayingVoicemail = () => {
      setVoicemailCurrentTime(0);
      updateProgressBarPosition(0);
    };

    /**
     * Called when the audio file metadata is ready
     */
    const audioPlayerMetaDataReady = () => {
      if (audioPlayer?.duration) {
        setVoicemailDuration(Math.floor(audioPlayer.duration));
      }
    };

    /**
     * Request to fetch the voicemail file
     */
    const fetchVoicemail = () => {
      setLoadingVoicemail(true);
      voicemailId && dispatch(fetchVoicemailFile(voicemailId));
    };

    /**
     * Called when the voicemail file is ready
     */
    const voicemailReady = () => {
      componentLoaded && enablePlayer && setLoadingVoicemail(false);
      if (!!audioPlayer && !audioPlayer.src && voicemailFilePath) {
        if (voicemailFilePath.startsWith("http")) {
          audioPlayer.src = voicemailFilePath;
          audioPlayer?.load();
        } else {
          let audioBase64 = "";
          const audioAsBlob = appConfig.fileStorage?.get(voicemailFilePath);
          const audioReader = new FileReader();
          audioReader.onloadend = () => {
            if (!audioPlayer || !enablePlayer) return;
            audioBase64 = audioReader.result as string;
            audioBase64 = audioBase64.replace(
              "data:application/octet-stream;base64,",
              "data:audio/ogg;base64, "
            );
            audioPlayer.src = audioBase64;
            audioPlayer?.load();
            playVoicemail();
          };
          audioReader.readAsDataURL(new Blob([(audioAsBlob as Buffer).buffer]));
        }
      }
    };

    /**
     * Reset the voicemail player to its initial state
     */
    const resetPlayer = (cleanUp?: boolean) => {
      setVoicemailDuration(0);
      setVoicemailCurrentTime(0);
      updateProgressBarPosition(0);
      setLoadingVoicemail(false);
      pauseVoicemail();
      if (cleanUp && audioPlayer) {
        audioPlayer.onended = () => {};          //
        audioPlayer.ontimeupdate = () => {};     // Clean event callbacks
        audioPlayer.onloadedmetadata = () => {}; //
        audioPlayer = null;
      }
    };

    /**
     * Handles any errors that accur when playing voicemails
     */
    const handleError = (error) => {
      console.error(error);

      dispatch(errorPlayingVoicemail(error));
    };

    if (error) {
      handleError(error);
    }

    const isPlaying: boolean = Boolean(
      audioPlayer && audioPlayer.src && !audioPlayer.paused
    );
    const action = isPlaying ? pauseVoicemail : playVoicemail;
    const icon = isPlaying ? "pause" : "play";
    const willPlayVoicemail = !isPlaying;

    return (
      <Grid style={{ ...removeMargin, ...removeVerticalPadding }}>
        <Grid.Column
          verticalAlign="middle"
          width={1}
          textAlign="left"
          style={{
            ...selectedStyle,
            padding: "0",
          }}
        >
          {loadingVoicemail ? (
            <LoadingIcon />
          ) : (
            drawPlayOrLoading()
          )}
        </Grid.Column>
        <Grid.Column
          verticalAlign="middle"
          width={2}
          textAlign="left"
          style={{
            ...selectedStyle,
            padding: "0",
          }}
          className="text-light"
        >
          {drawElapsedTime()}
        </Grid.Column>
        <Grid.Column
          verticalAlign="middle"
          style={{
            ...selectedStyle,
            padding: "0",
          }}
          width={10}
          textAlign="center"
        >
          {drawSlider(sliderValue)}
        </Grid.Column>
        <Grid.Column
          verticalAlign="middle"
          width={2}
          textAlign="right"
          style={{
            ...selectedStyle,
            padding: "0",
          }}
          className="text-light"
        >
          {drawTotalTime()}
        </Grid.Column>
      </Grid>
    );
  }
);

export default React.memo(VoicemailPlayer);
